import * as React from "react"
import { useContext, useMemo } from "react"
import { useAuth } from "@/hooks/useAuth"
import { useAssignableCompanies } from "@/hooks/useAssignableCompanies"
import { Company } from "@/domain/company"
import { decodeJwt, matsuriCompanyId } from "@/hooks/useAccountInfo"

interface CompanyAssignmentContextState {
    // 業者を選択してアサインできるか（Matsuriユーザー + 業者あり）
    canSelectCompany: boolean
    // 業者アサイン機能が有効か（ユーザー問わず）
    isCompanyAssignmentEnabled: boolean
    assignableCompanies: Company[]
}

const CompanyAssignmentContext = React.createContext<
    CompanyAssignmentContextState | undefined
>(undefined)

export const CompanyAssignmentProvider = ({
    children
}: {
    children: React.ReactNode
}) => {
    const { token } = useAuth()
    const jwtPayload = useMemo(() => decodeJwt(token), [token])
    const isMatsuri = jwtPayload?.companyId === matsuriCompanyId
    const { data: assignableCompanies } = useAssignableCompanies(token)

    const value = useMemo(
        () => ({
            canSelectCompany:
                isMatsuri && (assignableCompanies?.length ?? 0) > 0,
            isCompanyAssignmentEnabled: (assignableCompanies?.length ?? 0) > 0,
            assignableCompanies: assignableCompanies ?? []
        }),
        [isMatsuri, assignableCompanies]
    )

    return (
        <CompanyAssignmentContext.Provider value={value}>
            {children}
        </CompanyAssignmentContext.Provider>
    )
}

export const useCompanyAssignment = (): CompanyAssignmentContextState => {
    const ctx = useContext(CompanyAssignmentContext)
    if (!ctx) {
        return {
            canSelectCompany: false,
            isCompanyAssignmentEnabled: false,
            assignableCompanies: []
        }
    }
    return ctx
}
